// react
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

// components
import { Card } from '@/components/atoms/Card/Card';
import { Container } from '@/components/atoms/Container/Container';
import { Icon } from '@/components/atoms/Icon/Icon';

import { CardContainer } from '@/components/templates/CardContainer/CardContainer';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import { theme } from '@/theme';

/**
 * WelcomeActions give the user options on how to proceed.
 */
export const WelcomeActions = ({
  firstName,
  toManageCareCircles,
  toManageAccounts,
  toManageOrganizations,
  toManageReferringPartners,
  toManageActionPlan,
  toManageInvitedMembers,
  toManagePartnerProducts,
  toCaseload,
  toBacktests,
}: WelcomeActionsProps) => {
  // hooks
  const [width] = useWindowSize();

  return (
    <Container>
      <Wrapper>
        <ContentContainer>
          <div>
            <GreetingTitle>Hi {firstName}!</GreetingTitle>
            <GreetingText>What would you like to manage?</GreetingText>
          </div>
          <CardContainer
            maxColumns={width < theme.breakpoint.smValue ? 1 : 2}
            size={width < theme.breakpoint.smValue ? 'sm' : 'md'}
          >
            {toManageAccounts && (
              <StyledLink to={toManageAccounts}>
                <StyledCard>
                  <Icon icon='user' />
                  <div>Accounts</div>
                </StyledCard>
              </StyledLink>
            )}
            {toManageOrganizations && (
              <StyledLink to={toManageOrganizations}>
                <StyledCard>
                  <Icon icon='building' />
                  <div>Organisations</div>
                </StyledCard>
              </StyledLink>
            )}
            {toManageReferringPartners && (
              <StyledLink to={toManageReferringPartners}>
                <StyledCard>
                  <Icon icon='building' />
                  <div>Referring Partners</div>
                </StyledCard>
              </StyledLink>
            )}
            {toManageCareCircles && (
              <StyledLink to={toManageCareCircles}>
                <StyledCard>
                  <Icon icon='circle-heart' />
                  <div>Care Circles</div>
                </StyledCard>
              </StyledLink>
            )}
            {toManageActionPlan && (
              <StyledLink to={toManageActionPlan}>
                <StyledCard>
                  <Icon icon='clipboard-list' />
                  <div>Action Plan</div>
                </StyledCard>
              </StyledLink>
            )}
            {toManageInvitedMembers && (
              <StyledLink to={toManageInvitedMembers}>
                <StyledCard>
                  <Icon icon='envelope' />
                  <div>Invited Members</div>
                </StyledCard>
              </StyledLink>
            )}
            {toManagePartnerProducts && (
              <StyledLink to={toManagePartnerProducts}>
                <StyledCard>
                  <Icon icon='box' />
                  <div>Products</div>
                </StyledCard>
              </StyledLink>
            )}
            {toCaseload && (
              <StyledLink to={toCaseload}>
                <StyledCard>
                  <Icon icon='bookmark' />
                  <div>My Caseload</div>
                </StyledCard>
              </StyledLink>
            )}
            {toBacktests && (
              <StyledLink to={toBacktests}>
                <StyledCard>
                  <Icon icon='scale-balanced' />
                  <div>Backtests</div>
                </StyledCard>
              </StyledLink>
            )}
          </CardContainer>
        </ContentContainer>
      </Wrapper>
    </Container>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${(props) => props.theme.spacing[24]} 0;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      padding: ${props.theme.spacing[64]} 0;
    `)}
`;

const ContentContainer = styled.div`
  padding: 0 ${(props) => props.theme.spacing[12]};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 650px;
  gap: ${(props) => props.theme.spacing[32]};
`;

const GreetingTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['4xl']};
  color: ${(props) => props.theme.color.primary[80]};
  margin-bottom: ${(props) => props.theme.spacing[4]};
  text-align: center;
`;

const GreetingText = styled.div`
  font: ${(props) => props.theme.typography.body.lg.regular};
  text-align: center;
  color: ${(props) => props.theme.color.neutral[80]};
`;

const StyledCard = styled(Card)`
  text-align: center;
  font: ${(props) => props.theme.typography.body.md.regular};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  transition-duration: 0.2s;
  &:hover {
    text-decoration: none;
    transform: scale(1.05);
  }
`;

interface WelcomeActionsProps {
  firstName: string;
  toManageCareCircles?: string;
  toManageAccounts?: string;
  toManageOrganizations?: string;
  toManageReferringPartners?: string;
  toManageActionPlan?: string;
  toManageInvitedMembers?: string;
  toManagePartnerProducts?: string;
  toCaseload?: string;
  toBacktests?: string;
}

WelcomeActions.displayName = 'WelcomeActions';

export default WelcomeActions;
